import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, X } from 'lucide-react';
import { adviserAdminApi, type AdviserAdminData, type ProfileState } from './api';
import AdviserProfileView from './AdviserProfileView';
import { profileReviewLabels, profileReviewStage } from './profileReview';
import './Advisers.css';

export type AdviserAdminTab = 'practices' | 'profiles' | 'featured';
export default function AdviserAdminSection({
  isVisible,
  onClose,
  inline = false,
  activeTab,
  onTabChange,
  search = '',
  focusPracticeId,
  onChanged,
  onPracticeDetails,
}: {
  isVisible: boolean;
  onClose: () => void;
  inline?: boolean;
  activeTab?: AdviserAdminTab;
  onTabChange?: (tab: AdviserAdminTab) => void;
  search?: string;
  focusPracticeId?: string;
  onChanged?: () => void;
  onPracticeDetails?: (id: string) => void;
}) {
  const [data, setData] = useState<AdviserAdminData | null>(null);
  const [localTab, setLocalTab] = useState<AdviserAdminTab>('practices');
  const tab = activeTab || localTab;
  const setTab = (next: AdviserAdminTab) => {
    setLocalTab(next);
    onTabChange?.(next);
  };
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [reason, setReason] = useState('');
  const [featuredIds, setFeaturedIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<(ProfileState & { practiceName: string }) | null>(null);
  const refresh = async () => {
    const next = await adviserAdminApi.list();
    setData(next);
    setFeaturedIds(
      [...next.featured].sort((a, b) => a.position - b.position).map((item) => item.practiceId)
    );
  };
  useEffect(() => {
    if (!isVisible) return;
    let active = true;
    setLoading(true);
    setError('');
    adviserAdminApi
      .list()
      .then((next) => {
        if (active) {
          setData(next);
          setFeaturedIds(
            [...next.featured]
              .sort((a, b) => a.position - b.position)
              .map((item) => item.practiceId)
          );
        }
      })
      .catch((err) => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isVisible]);
  if (!isVisible) return null;
  const action = async (run: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await run();
      await refresh();
      onChanged?.();
      setNotice(message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save this review.');
    } finally {
      setBusy(false);
    }
  };
  const eligible =
    data?.profiles.filter(
      (profile) =>
        Boolean(profile.published) &&
        profile.approvalStatus === 'approved' &&
        data.practices.some(
          (practice) => practice.id === profile.practiceId && practice.status === 'approved'
        )
    ) || [];
  const query = search.trim().toLowerCase();
  const practices =
    data?.practices.filter(
      (practice) =>
        (!focusPracticeId || practice.id === focusPracticeId) &&
        `${practice.name} ${practice.billingEmail || ''}`.toLowerCase().includes(query)
    ) || [];
  const profiles =
    data?.profiles.filter(
      (profile) =>
        (!focusPracticeId || profile.practiceId === focusPracticeId) &&
        profile.practiceName.toLowerCase().includes(query)
    ) || [];
  const move = (index: number, direction: number) => {
    setFeaturedIds((current) => {
      const next = [...current];
      const target = index + direction;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };
  return (
    <div className={inline ? undefined : 'adviser-admin-backdrop'}>
      <section
        className={`adviser-admin-dialog${inline ? ' adviser-admin-inline' : ''}`}
        role={inline ? undefined : 'dialog'}
        aria-modal={inline ? undefined : true}
        aria-labelledby={inline ? undefined : 'adviser-admin-title'}
        aria-label={inline ? 'Adviser reviews' : undefined}
      >
        {!inline && (
          <div className="adviser-section-heading">
            <div>
              <p className="adviser-eyebrow">Application administration</p>
              <h2 id="adviser-admin-title">Adviser programme</h2>
            </div>
            {!inline && (
              <button
                aria-label="Close adviser administration"
                type="button"
                className="adviser-button adviser-button-quiet"
                onClick={onClose}
              >
                <X />
              </button>
            )}
          </div>
        )}
        {!inline && (
          <nav className="adviser-admin-tabs" aria-label="Adviser administration">
            <button
              type="button"
              aria-current={tab === 'practices' ? 'page' : undefined}
              onClick={() => setTab('practices')}
            >
              Applications & practices
            </button>
            <button
              type="button"
              aria-current={tab === 'profiles' ? 'page' : undefined}
              onClick={() => setTab('profiles')}
            >
              Profile approval
            </button>
            <button
              type="button"
              aria-current={tab === 'featured' ? 'page' : undefined}
              onClick={() => setTab('featured')}
            >
              Homepage curation
            </button>
          </nav>
        )}
        {error && (
          <p className="adviser-error" role="alert">
            {error}
          </p>
        )}
        {notice && (
          <p className="adviser-success" role="status">
            {notice}
          </p>
        )}
        {loading ? (
          <p className="adviser-empty">Loading adviser reviews…</p>
        ) : !data ? (
          <button
            className="adviser-button"
            type="button"
            onClick={() => void action(refresh, 'Reviews loaded.')}
          >
            Try again
          </button>
        ) : (
          <>
            {tab !== 'featured' && (
              <label className="adviser-review-reason">
                Review note
                <input
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Reason or decision context, recorded in the audit history"
                  maxLength={2000}
                />
              </label>
            )}
            {tab === 'practices' && (
              <div>
                {practices.length ? (
                  practices.map((practice) => (
                    <article className="adviser-panel" key={practice.id}>
                      <div className="adviser-section-heading">
                        <div>
                          <h3>{practice.name}</h3>
                          <p>
                            {practice.billingEmail} · Applied{' '}
                            {new Date(practice.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`adviser-status adviser-status-${practice.status}`}>
                          {practice.status}
                        </span>
                      </div>
                      {practice.website && <p>{practice.website}</p>}
                      <p className="adviser-preserve-lines">{practice.applicationNotes}</p>
                      <div className="adviser-actions">
                        {onPracticeDetails && (
                          <button
                            type="button"
                            className="adviser-button adviser-button-secondary"
                            onClick={() => onPracticeDetails(practice.id)}
                          >
                            View connections
                          </button>
                        )}
                        {practice.status !== 'approved' && (
                          <button
                            className="adviser-button"
                            disabled={busy}
                            type="button"
                            onClick={() =>
                              void action(
                                () => adviserAdminApi.status(practice.id, 'approved', reason),
                                'Practice approved.'
                              )
                            }
                          >
                            Approve practice
                          </button>
                        )}
                        {practice.status === 'pending' && (
                          <button
                            className="adviser-button adviser-button-secondary"
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              void action(
                                () => adviserAdminApi.status(practice.id, 'rejected', reason),
                                'Application rejected.'
                              )
                            }
                          >
                            Reject application
                          </button>
                        )}
                        {practice.status === 'approved' && (
                          <button
                            className="adviser-button adviser-button-quiet"
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Suspend ${practice.name}? Delegated client access and public visibility will stop. Client data and billing commitments remain.`
                                )
                              )
                                void action(
                                  () => adviserAdminApi.status(practice.id, 'suspended', reason),
                                  'Practice suspended.'
                                );
                            }}
                          >
                            Suspend practice
                          </button>
                        )}
                      </div>
                    </article>
                  ))
                ) : (
                  <p className="adviser-empty">No adviser applications yet.</p>
                )}
              </div>
            )}
            {tab === 'profiles' && (
              <div>
                {profiles.length ? (
                  profiles.map((profile) => (
                    <article className="adviser-panel" key={profile.practiceId}>
                      <div className="adviser-section-heading">
                        <div>
                          <h3>{profile.practiceName}</h3>
                          <p>
                            /advisers/{profile.slug} ·{' '}
                            {profile.published ? 'Published' : 'Private draft'}
                          </p>
                        </div>
                        <span className="adviser-status">
                          {profileReviewLabels[profileReviewStage(profile)]}
                        </span>
                      </div>
                      <p>{profile.draft.headline}</p>
                      <div className="adviser-actions">
                        <button
                          className="adviser-button adviser-button-secondary"
                          type="button"
                          onClick={() => setPreview(profile)}
                        >
                          Review full draft
                        </button>
                        {profile.approvalStatus !== 'approved' && (
                          <button
                            className="adviser-button"
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              void action(
                                () =>
                                  adviserAdminApi.reviewProfile(profile.practiceId, true, reason),
                                'Initial profile approved. The practice owner can publish it.'
                              )
                            }
                          >
                            Approve initial profile
                          </button>
                        )}
                        <button
                          className="adviser-button adviser-button-quiet"
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            void action(
                              () =>
                                adviserAdminApi.reviewProfile(profile.practiceId, false, reason),
                              'Profile approval withdrawn and public visibility removed.'
                            )
                          }
                        >
                          {profile.approvalStatus === 'approved'
                            ? 'Withdraw approval'
                            : 'Request changes'}
                        </button>
                      </div>
                    </article>
                  ))
                ) : (
                  <p className="adviser-empty">No profiles submitted for review yet.</p>
                )}
              </div>
            )}
            {tab === 'featured' && (
              <div>
                <h3>Featured homepage firms</h3>
                <p>
                  Select approved, published firms and arrange the order. Unpublished or suspended
                  firms automatically disappear from public discovery.
                </p>
                <div className="adviser-panel">
                  {featuredIds.map((id, index) => {
                    const profile = data.profiles.find((item) => item.practiceId === id);
                    return (
                      <div className="adviser-team-row" key={id}>
                        <strong>
                          {index + 1}. {profile?.practiceName || id}
                        </strong>
                        <div className="adviser-actions">
                          <button
                            className="adviser-button adviser-button-quiet"
                            aria-label={`Move ${profile?.practiceName} up`}
                            type="button"
                            disabled={index === 0}
                            onClick={() => move(index, -1)}
                          >
                            <ArrowUp size={16} />
                          </button>
                          <button
                            className="adviser-button adviser-button-quiet"
                            aria-label={`Move ${profile?.practiceName} down`}
                            type="button"
                            disabled={index === featuredIds.length - 1}
                            onClick={() => move(index, 1)}
                          >
                            <ArrowDown size={16} />
                          </button>
                          <button
                            type="button"
                            className="adviser-button adviser-button-quiet"
                            onClick={() =>
                              setFeaturedIds((current) => current.filter((value) => value !== id))
                            }
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {!featuredIds.length && (
                    <p>No featured firms selected. The homepage section will be hidden.</p>
                  )}
                </div>
                <h3>Available firms</h3>
                <div className="adviser-panel">
                  {eligible
                    .filter((profile) => !featuredIds.includes(profile.practiceId))
                    .map((profile) => (
                      <div className="adviser-team-row" key={profile.practiceId}>
                        <strong>{profile.practiceName}</strong>
                        <button
                          type="button"
                          className="adviser-button adviser-button-secondary"
                          onClick={() =>
                            setFeaturedIds((current) => [...current, profile.practiceId])
                          }
                        >
                          Feature firm
                        </button>
                      </div>
                    ))}
                  {!eligible.length && <p>No approved, published firms available.</p>}
                </div>
                <button
                  className="adviser-button"
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void action(
                      () => adviserAdminApi.feature(featuredIds),
                      'Homepage selection and order saved.'
                    )
                  }
                >
                  Save homepage selection
                </button>
              </div>
            )}
          </>
        )}
        {preview && (
          <div className="adviser-modal-backdrop">
            <section
              className="adviser-preview-modal"
              role="dialog"
              aria-modal="true"
              aria-label={`Review ${preview.practiceName}`}
            >
              <div className="adviser-section-heading">
                <h2>{preview.practiceName} · initial profile review</h2>
                <button
                  className="adviser-button adviser-button-secondary"
                  type="button"
                  onClick={() => setPreview(null)}
                >
                  Close preview
                </button>
              </div>
              <AdviserProfileView profile={preview.draft} preview />
            </section>
          </div>
        )}
      </section>
    </div>
  );
}
